import React, { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import { useChatPlayContext } from '@provider/ChatPlayProvider';
import { ScreenFillMethod, TransitionStyle } from '@/customTypes/chatStory';

import { Portal } from '@components/commons';
import Image from 'next/image';

interface IStyleOptions {
  sizeRatio: number;
  fillMethod: ScreenFillMethod;
  showTransition: TransitionStyle;
  hideTransition: TransitionStyle;
  duration: number;
  isHide: boolean;
}

const FullScreenImagePopup = () => {
  const { fullScreenImagePopupInfo, setFullScreenImagePopupInfo } =
    useChatPlayContext();
  const [isHide, setIsHide] = useState(false);

  useEffect(() => {
    return () => {
      setIsHide(false);
    };
  }, [fullScreenImagePopupInfo]);

  const handleImageClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    setIsHide(true);
    if (fullScreenImagePopupInfo?.hideTransition === 'None') {
      setFullScreenImagePopupInfo(null);
    } else {
      const duration = fullScreenImagePopupInfo?.duration || 300;
      setTimeout(() => {
        setFullScreenImagePopupInfo(null);
      }, duration - 10); // 스타일 duration보다 먼저 끝나야해서 -10
    }

    e.stopPropagation();
  };

  if (!fullScreenImagePopupInfo) {
    return null;
  }

  return (
    <Portal>
      <div
        css={wrapper({
          sizeRatio:
            (fullScreenImagePopupInfo.height! /
              fullScreenImagePopupInfo.width!) *
            100,
          fillMethod: fullScreenImagePopupInfo.fillMethod,
          showTransition: fullScreenImagePopupInfo?.showTransition,
          hideTransition: fullScreenImagePopupInfo?.hideTransition,
          duration: fullScreenImagePopupInfo.duration,
          isHide,
        })}
        id="full-screen-popup"
        onClick={handleImageClick}
      >
        <div className="image-wrapper">
          {fullScreenImagePopupInfo.fullScreenImageLink && (
            <>
              {fullScreenImagePopupInfo.fillMethod === 'AspectRatio' ? (
                <Image
                  className="image"
                  src={fullScreenImagePopupInfo.fullScreenImageLink}
                  layout="fill"
                  objectFit="cover"
                />
              ) : (
                <Image
                  className="image"
                  src={fullScreenImagePopupInfo.fullScreenImageLink}
                  layout="fill"
                />
              )}
            </>
          )}
        </div>
      </div>
    </Portal>
  );
};

const wrapper = ({
  fillMethod,
  sizeRatio,
  showTransition,
  hideTransition,
  duration,
  isHide,
}: IStyleOptions) => css`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: ${theme.maxWidth};
  height: 100vh;
  z-index: 10;
  background: black;

  .image-wrapper {
    position: relative;
    width: 100%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    ${fillMethod === 'AspectRatio'
      ? `padding-top:${sizeRatio}%;
      `
      : `height: 100vh;`}
  }

  ${isHide &&
  hideTransition === 'Zoom' &&
  `animation: zoomOut ${duration}ms ease-in-out;`}
  ${!isHide &&
  showTransition === 'Zoom' &&
  `animation: zoomIn ${duration}ms ease-in-out;`};
  ${isHide &&
  hideTransition === 'Fade' &&
  `animation: fadeOut ${duration}ms ease-in-out;`}
  ${!isHide &&
  showTransition === 'Fade' &&
  `animation: fadeIn ${duration}ms ease-in-out;`};

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
  @keyframes zoomIn {
    0% {
      transform: translate(-50%, -50%) scale(0);
    }
    100% {
      transform: translate(-50%, -50%) scale(1);
    }
  }
  @keyframes zoomOut {
    0% {
      transform: translate(-50%, -50%) scale(1);
    }
    100% {
      transform: translate(-50%, -50%) scale(0);
    }
  }
`;

export default FullScreenImagePopup;
